import { useEffect, useState } from "react";
import { Share, Smartphone, X, Download, PlusSquare } from "lucide-react";
import "./install-prompt.css";

const KEY = "installPrompt:dismissed:v1";
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent);
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

/**
 * Sugerencia de instalar el panel como app. Android/Chrome/Edge: botón que dispara
 * el prompt nativo (beforeinstallprompt). iPhone: instrucciones (Safari no expone
 * prompt). No aparece si ya está instalada; "Ahora no" la oculta 14 días.
 */
export default function InstallPrompt({ appName = "tu panel" }) {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosSteps, setShowIosSteps] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    try {
      const until = Number(localStorage.getItem(KEY) || 0);
      if (until > Date.now()) return;
    } catch { /* sin storage */ }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setVisible(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // iOS nunca dispara el evento: mostrar instrucciones directamente.
    if (isIos()) setVisible(true);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(KEY, String(Date.now() + 14 * 864e5)); } catch { /* noop */ }
  };

  const install = async () => {
    if (!deferred) { setShowIosSteps(true); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") setVisible(false);
    setDeferred(null);
  };

  if (!visible) return null;
  const ios = isIos();

  return (
    <div className="ip-card" role="region" aria-label="Instalar la app" data-tour="install">
      <div className="ip-icon"><Smartphone size={20} /></div>
      <div className="ip-text">
        <div className="ip-title">Instala {appName} como app</div>
        <div className="ip-body">
          {ios && showIosSteps ? (
            <ol className="ip-steps">
              <li>Toca <Share size={14} className="ip-inline" /> <b>Compartir</b> en la barra de Safari.</li>
              <li>Elige <PlusSquare size={14} className="ip-inline" /> <b>Agregar a pantalla de inicio</b>.</li>
              <li>Ábrela desde el ícono: ahí sí puedes activar las notificaciones.</li>
            </ol>
          ) : (
            "Un ícono en tu teléfono: abre más rápido, sin la barra del navegador, y con notificaciones de cada cita."
          )}
        </div>
        <div className="ip-actions">
          {!(ios && showIosSteps) && (
            <button type="button" className="ip-btn ip-btn-primary" onClick={install}>
              <Download size={15} /> {ios ? "Cómo instalarla" : "Instalar"}
            </button>
          )}
          <button type="button" className="ip-btn" onClick={dismiss}>Ahora no</button>
        </div>
      </div>
      <button type="button" className="ip-x" onClick={dismiss} aria-label="Cerrar"><X size={16} /></button>
    </div>
  );
}
